/**
 * Small reusable pieces used across master screens.
 */
import { Search } from "lucide-react";

export const StatusBadge = ({ active }) => (
  <span
    className="chip"
    data-testid={`status-badge-${active ? "active" : "inactive"}`}
    style={
      active
        ? {
            backgroundColor: "rgba(39, 174, 96, 0.14)",
            color: "#1e8a4a",
            border: "1px solid rgba(39, 174, 96, 0.35)",
          }
        : {
            backgroundColor: "rgba(52, 73, 94, 0.08)",
            color: "var(--vision-slate)",
            border: "1px solid rgba(52, 73, 94, 0.20)",
          }
    }
  >
    <span
      className="inline-block h-1.5 w-1.5 rounded-full"
      style={{ backgroundColor: active ? "var(--vision-success)" : "var(--vision-slate)" }}
    />
    {active ? "Active" : "Inactive"}
  </span>
);

export const SearchBox = ({ value, onChange, placeholder = "Search…", testId = "search-input" }) => (
  <div
    className="flex items-center gap-2 rounded-full border bg-[color:var(--vision-ice)] px-3 py-2"
    style={{ borderColor: "rgba(52,73,94,0.10)" }}
  >
    <Search className="h-4 w-4 text-vision-slate/70" />
    <input
      data-testid={testId}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-56 bg-transparent text-sm outline-none placeholder:text-vision-slate/50"
    />
  </div>
);

export const PrimaryButton = ({ children, testId, ...rest }) => (
  <button
    data-testid={testId}
    {...rest}
    className="btn-pill bg-[color:var(--vision-orange)] px-5 py-2.5 text-sm font-semibold text-white shadow-card transition hover:brightness-105 hover:shadow-card-lg disabled:opacity-60"
  >
    {children}
  </button>
);

export const OutlineButton = ({ children, testId, ...rest }) => (
  <button
    data-testid={testId}
    {...rest}
    className="btn-pill border border-[color:var(--vision-navy)] px-4 py-2 text-sm font-semibold text-vision-navy transition hover:bg-[color:var(--vision-navy)] hover:text-white disabled:opacity-60"
  >
    {children}
  </button>
);

export const GhostButton = ({ children, testId, ...rest }) => (
  <button
    data-testid={testId}
    {...rest}
    className="chip bg-[color:var(--vision-ice)] text-vision-navy transition hover:bg-[color:var(--vision-peach)] disabled:opacity-60"
  >
    {children}
  </button>
);

export const Field = ({ label, hint, children, error }) => (
  <label className="flex flex-col gap-1.5">
    <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-vision-slate/70">
      {label}
    </span>
    {children}
    {hint && !error && <span className="text-[11px] text-vision-slate/50">{hint}</span>}
    {error && (
      <span
        className="text-[11px] font-medium"
        style={{ color: "var(--vision-error)" }}
      >
        {error}
      </span>
    )}
  </label>
);

export const inputCls =
  "w-full rounded-xl border bg-white px-4 py-2.5 text-sm text-vision-ink outline-none transition focus:border-[color:var(--vision-orange)] focus:ring-2 focus:ring-[color:var(--vision-orange)]/25";

export const selectCls = inputCls + " vision-select";


// ---------------------------------------------------------------------------
// Shared list-row Chip used by Sessions History and Allocation History.
// ---------------------------------------------------------------------------
export const chipToneStyle = {
  success: {
    backgroundColor: "rgba(39, 174, 96, 0.14)",
    color: "#1e8a4a",
    border: "1px solid rgba(39, 174, 96, 0.35)",
  },
  navy: {
    backgroundColor: "rgba(0, 53, 158, 0.10)",
    color: "var(--vision-navy)",
    border: "1px solid rgba(0, 53, 158, 0.25)",
  },
  orange: {
    backgroundColor: "rgba(247, 144, 9, 0.14)",
    color: "var(--vision-orange)",
    border: "1px solid rgba(247, 144, 9, 0.35)",
  },
  slate: {
    backgroundColor: "rgba(52, 73, 94, 0.08)",
    color: "var(--vision-slate)",
    border: "1px solid rgba(52, 73, 94, 0.20)",
  },
};

export const HistoryChip = ({ tone, icon: Icon, label, testId }) => (
  <span className="chip" style={chipToneStyle[tone]} data-testid={testId}>
    {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
    {label}
  </span>
);
